import React, { useState, useEffect } from "react";
import { useHR } from "../HRContext";
import { HRPosition, PositionFormData, HRDepartment } from "../../types/hr";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Button,
  message,
  Spin,
  Divider,
} from "antd";

const { Option } = Select;
const { TextArea } = Input;

interface PositionFormProps {
  open: boolean;
  position?: HRPosition | null;
  onClose: () => void;
  selectedDepartmentId?: number | null;
}

const PositionForm: React.FC<PositionFormProps> = ({
  open,
  position,
  onClose,
  selectedDepartmentId,
}) => {
  const { departments, createPosition, updatePosition } = useHR();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (position) {
      form.setFieldsValue({
        title: position.title || "",
        description: position.description || "",
        department_id: position.department_id || undefined,
        min_salary: position.min_salary || undefined,
        max_salary: position.max_salary || undefined,
        requirements: position.requirements || "",

        status: position.status || "active",
      });
    } else {
      form.resetFields();
      // Si un département est présélectionné, l'utiliser par défaut
      if (selectedDepartmentId) {
        form.setFieldsValue({
          department_id: selectedDepartmentId,
          status: "active",
        });
      } else {
        form.setFieldsValue({
          status: "active",
        });
      }
    }
  }, [position, form, selectedDepartmentId]);

  const handleSubmit = async (values: PositionFormData) => {
    setLoading(true);
    try {
      // Validation des salaires
      if (
        values.min_salary &&
        values.max_salary &&
        values.min_salary > values.max_salary
      ) {
        message.error(
          "Le salaire minimum ne peut pas être supérieur au salaire maximum"
        );
        setLoading(false);
        return;
      }

      if (position) {
        await updatePosition(position.id, values);
        message.success("Poste modifié avec succès");
      } else {
        await createPosition(values);
        message.success("Poste créé avec succès");
      }
      onClose();
    } catch (error) {
      message.error("Erreur lors de la sauvegarde du poste");
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les départements actifs
  const availableDepartments = departments.filter(
    (dept: HRDepartment) => dept.is_active
  );

  return (
    <Modal
      title={position ? "Modifier le poste" : "Nouveau poste"}
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: "active",
          }}
        >
          {/* Informations de base */}
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="title"
                label="Titre du poste"
                rules={[
                  {
                    required: true,
                    message: "Le titre du poste est requis",
                  },
                ]}
              >
                <Input placeholder="Ex: Développeur Full Stack" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="department_id"
                label="Département"
                rules={[
                  {
                    required: true,
                    message: "Le département est requis",
                  },
                ]}
              >
                <Select
                  placeholder="Sélectionner un département"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {availableDepartments.map((dept: HRDepartment) => (
                    <Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description du poste">
            <TextArea
              rows={3}
              placeholder="Description générale du poste et de son rôle dans l'entreprise..."
            />
          </Form.Item>

          <Divider>Fourchette salariale</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="min_salary" label="Salaire minimum (FCFA)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  formatter={(value) =>
                    value
                      ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                      : ""
                  }
                  placeholder="Salaire minimum"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="max_salary" label="Salaire maximum (FCFA)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  formatter={(value) =>
                    value
                      ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                      : ""
                  }
                  placeholder="Salaire maximum"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Détails du poste</Divider>

          <Form.Item name="requirements" label="Exigences et qualifications">
            <TextArea
              rows={6}
              placeholder="• Diplôme en informatique ou équivalent&#10;• 3+ années d'expérience en développement web&#10;• Maîtrise de React, Node.js&#10;• Responsabilités principales du poste&#10;• Missions et objectifs attendus..."
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="Statut"
            rules={[{ required: true, message: "Le statut est requis" }]}
          >
            <Select>
              <Option value="active">Actif</Option>
              <Option value="inactive">Inactif</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <Button onClick={onClose}>Annuler</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {position ? "Modifier" : "Créer"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default PositionForm;
